import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { toast } from 'react-toastify';
import '../assets/css/toast.css';

interface RequestBody {
  operationName: string;
}

const errorLink = onError(({ networkError }) => {
  // For handling dynamic forms validations errors.

  function compileErrorMsg(): string {
    if (networkError && 'result' in networkError) {
      if (typeof networkError.result === 'string') {
        return networkError.result;
      }
      if (networkError.result.errors) {
        // eslint-disable-next-line
        return networkError.result.errors[0].message;
      }
    }
    return 'Unexpected error';
  }

  if (networkError && 'result' in networkError) {
    // For handling authentication errors.
    if (networkError?.statusCode === 401 || networkError?.statusCode === 403) {
      window.location.href = `https://${process.env.REACT_APP_AUTH_FE_BASE_URL}/login`;
      return;
    }
    if (networkError?.statusCode === 406 || networkError?.statusCode === 421) {
      toast.error(`${compileErrorMsg()}`, {
        toastId: 'error-toast',
        position: 'top-right',
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: 'customToast',
      });
      return;
    }

    toast.error(`Error message: ${compileErrorMsg()}`, {
      toastId: 'error-toast',
      position: 'bottom-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }
});

const customFetch = (_: unknown, options: RequestInit | undefined): Promise<Response> => {
  if (!options || !options.body) {
    throw new Error('Request is not valid!');
  }
  const body = JSON.parse(options.body as string) as RequestBody;
  // Follow the naming convention for all the queries created on front-end. The name should go:
  // SERVICE_NAME_nameOfQuery . The service should also be listed here, in customFetch function.
  if (body.operationName.includes('DOMAIN_SERVICE_')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/domainV2/Prod/graphqlNoAuth`, options);
  }
  if (body.operationName.includes('CART_SERVICE_')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/cartV2/Prod/graphqlNoAuth`, options);
  }
  if (body.operationName.includes('ENROLLMENT_SERVICE_')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/enrollment/Prod/graphqlNoAuth`, options);
  }
  if (body.operationName.includes('GEO_SERVICE_')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/geo/Prod/graphql`, options);
  }
  if (body.operationName.includes('GROUP_SERVICE_')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/groupV2/Prod/graphqlNoAuth`, options);
  }
  if (body.operationName.includes('PRODUCT_SERVICE_')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/productV2/Prod/graphqlNoAuth`, options);
  }
  if (body.operationName.includes('FORM_SERVICE_')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/form/Prod/graphqlNoAuth`, options);
  }
  if (body.operationName.includes('USER_SERVICE_')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/userV2/Prod/graphqlNoAuth`, options);
  }
  if (body.operationName.includes('ZYWAVE_SERVICE_')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/zywave/Prod/graphqlNoAuth`, options);
  }
  throw new Error('Unknown server requested!');
};
const link = createHttpLink({ fetch: customFetch, credentials: 'include' });

const additiveLink = ApolloLink.from([errorLink, link]);

const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    addTypename: false,
  }),
});
apolloClient.setLink(additiveLink);

export default apolloClient;
